import React from 'react';
import { Check, Star, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { packagesData } from '../data/mock';

const Packages = () => {
  const scrollToContact = () => {
    const el = document.getElementById('contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="packages"
      data-testid="packages-section"
      className="relative py-28 lg:py-36"
      style={{ backgroundColor: '#0e1629' }}
    >
      <div className="absolute inset-0 bg-grid bg-grid-fade opacity-30 pointer-events-none" />
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-violet-600/10 blur-[160px] rounded-full pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-16 lg:mb-20">
          <span className="inline-block text-xs font-bold uppercase tracking-[0.22em] text-violet-300 mb-5">
            Csomagok
          </span>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-[3.25rem] font-semibold text-white leading-[1.05] tracking-tight text-balance" data-testid="packages-title">
            Válaszd ki a<span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(120deg,#a78bfa,#e879f9)' }}> növekedési terved</span>
          </h2>
          <p className="mt-4 text-lg text-slate-400">
            Átlátható, rugalmas csomagok – induló vállalkozástól a skálázódó cégig.
          </p>
        </div>

        {/* Grid */}
        <div className="grid md:grid-cols-3 gap-5 lg:gap-6 items-stretch">
          {packagesData.map((pkg, index) => {
            const isHighlight = pkg.highlight;
            const Wrapper = ({ children }) =>
              isHighlight ? (
                <div className="relative rounded-[2rem] p-[1.5px] bg-gradient-to-b from-violet-500 via-fuchsia-500 to-sky-500 shadow-[0_30px_80px_-20px_rgba(217,70,239,0.4)] lg:-mt-4">
                  {children}
                </div>
              ) : (
                <>{children}</>
              );

            return (
              <motion.div
                key={pkg.id}
                data-testid={`package-card-${pkg.id}`}
                initial={{ opacity: 0, y: 36 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.7, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="relative h-full"
              >
                <Wrapper>
                  <div
                    className={`relative h-full rounded-[calc(2rem-2px)] overflow-hidden transition-all duration-500 group
                      ${isHighlight
                        ? 'bg-slate-900 text-white'
                        : 'bg-slate-50 ring-1 ring-slate-900/5 hover:-translate-y-1.5 shadow-[0_14px_40px_-20px_rgba(15,23,42,0.8)]'
                      } p-8 lg:p-10`}
                  >
                    {/* Popular badge */}
                    {isHighlight && (
                      <div className="absolute top-5 right-5 inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur px-3 py-1">
                        <Star className="w-3.5 h-3.5 text-fuchsia-300" fill="currentColor" />
                        <span className="text-[11px] font-semibold tracking-wider uppercase text-white">Legnépszerűbb</span>
                      </div>
                    )}

                    {/* Highlight glow */}
                    {isHighlight && (
                      <>
                        <div className="absolute -top-24 -right-20 w-60 h-60 rounded-full bg-fuchsia-500/30 blur-3xl pointer-events-none" />
                        <div className="absolute -bottom-24 -left-10 w-72 h-72 rounded-full bg-violet-500/20 blur-3xl pointer-events-none" />
                        <div className="grain-overlay" />
                      </>
                    )}

                    {/* Header */}
                    <div className="relative">
                      <h3 className={`font-display text-2xl font-semibold tracking-tight mb-1.5 ${isHighlight ? 'text-white' : 'text-slate-900'}`}>
                        {pkg.name}
                      </h3>
                      <p className={`text-sm ${isHighlight ? 'text-slate-300' : 'text-slate-500'}`}>
                        {pkg.description}
                      </p>
                    </div>

                    {/* Divider */}
                    <div className={`relative my-7 h-px ${isHighlight ? 'bg-white/10' : 'bg-slate-200'}`} />

                    {/* Features */}
                    <ul className="relative space-y-3.5 mb-9">
                      {pkg.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <span className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                            isHighlight ? 'bg-white/10 ring-1 ring-white/20' : 'bg-violet-100 ring-1 ring-violet-200'
                          }`}>
                            <Check className={`w-3 h-3 ${isHighlight ? 'text-fuchsia-300' : 'text-violet-700'}`} strokeWidth={3} />
                          </span>
                          <span className={isHighlight ? 'text-slate-200 text-[15px]' : 'text-slate-700 text-[15px]'}>
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>

                    {/* CTA */}
                    <button
                      data-testid={`package-cta-${pkg.id}`}
                      onClick={scrollToContact}
                      className={`shine group/btn relative w-full inline-flex items-center justify-center gap-2 py-3.5 font-semibold rounded-full transition-all duration-400
                        ${isHighlight
                          ? 'bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white hover:from-violet-400 hover:to-fuchsia-400 shadow-[0_12px_40px_-12px_rgba(217,70,239,0.6)]'
                          : 'bg-slate-900 text-white hover:bg-slate-800'
                        }`}
                    >
                      Ajánlatot kérek
                      <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover/btn:translate-x-1" />
                    </button>
                  </div>
                </Wrapper>
              </motion.div>
            );
          })}
        </div>

        <p className="text-center text-slate-500 mt-10 text-sm">
          Az árak egyedi kalkuláción alapulnak. Kérj személyre szabott ajánlatot!
        </p>
      </div>
    </section>
  );
};

export default Packages;
